/**
 * Work → Tasks (/work). The everyday to-do screen: type a task, see today
 * (late, today, no date yet), the next two weeks, the routines that run the
 * business, and the work handed to AI employees. Everything is owned by a
 * person; an AI employee can do it under them.
 *
 * URL: ?view=today|upcoming|routines|ai  ?person=me|all|<user id>
 *      ?kind=all|clients|mine  ?task=<id> opens one.
 *
 * Clients = tasks about a person (linked to a contact) or in the Customers
 * area; My work = everything else. Within each group urgent and high
 * priority come first (features/work/views.tsx).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CalendarDays, Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { api, ApiError } from "@/lib/api";
import { Button, Skeleton, useToast } from "@/components/ui";
import { Card, PageHeader, Segmented } from "@/features/command/ui";
import { QuickAdd } from "@/features/command/QuickAdd";
import { TaskDrawer, type TaskTarget } from "@/features/work/TaskDrawer";
import { PersonSwitch, personId, storePerson, storedPerson } from "@/features/work/PersonSwitch";
import { AiView, RoutinesView, TodayView, UpcomingView, type ViewProps } from "@/features/work/views";
import { addDays, dueDay, findTask, todayYmd, useLookups, useMe } from "@/features/work/lib";
import type { WorkTask } from "@/features/work/types";

type View = "today" | "upcoming" | "routines" | "ai";
const VIEWS: View[] = ["today", "upcoming", "routines", "ai"];
const VIEW_KEY = "cc.work.view";

type Kind = "all" | "clients" | "mine";
const KINDS: Kind[] = ["all", "clients", "mine"];
const KIND_KEY = "cc.work.kind";
function storedKind(): Kind {
  try { const v = localStorage.getItem(KIND_KEY) as Kind | null; return v && KINDS.includes(v) ? v : "all"; } catch { return "all"; }
}
const isClientTask = (t: WorkTask) => !!t.contact_id || t.area === "customers";

function storedView(): View {
  try { const v = localStorage.getItem(VIEW_KEY) as View | null; return v && VIEWS.includes(v) ? v : "today"; } catch { return "today"; }
}

export function WorkPage() {
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const { people, team, wsTz, loaded } = useLookups();
  const { me, ready } = useMe();
  const viewParam = params.get("view") as View | null;
  const view: View = viewParam && VIEWS.includes(viewParam) ? viewParam : storedView();
  const person = params.get("person") || storedPerson();
  const kindParam = params.get("kind") as Kind | null;
  const kind: Kind = kindParam && KINDS.includes(kindParam) ? kindParam : storedKind();
  const assignee = personId(person, me, people);
  const taskParam = params.get("task");

  const [tasks, setTasks] = useState<WorkTask[] | null>(null);
  const [doneLocal, setDoneLocal] = useState<Record<string, WorkTask>>({});
  const [drawer, setDrawer] = useState<TaskTarget | null>(null);

  const load = useCallback(async () => {
    try {
      const d = await api.get<{ tasks: WorkTask[] }>("/tasks", { status: "open", limit: 500, assignee_id: assignee });
      setTasks(d.tasks);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't load tasks");
      setTasks((x) => x ?? []);
    }
  }, [assignee, toast]);

  // Wait until we know who "me" is and who the team is, so the first list is the right one.
  useEffect(() => { if (ready && loaded) void load(); }, [ready, loaded, load]);
  useEffect(() => { setDoneLocal({}); }, [assignee]);

  // ?task=<id> (from Home, Team, a source page) opens that task.
  useEffect(() => {
    if (!taskParam) return;
    const hit = tasks?.find((t) => t.id === taskParam);
    if (hit) { setDrawer(hit); return; }
    if (tasks === null) return;
    let live = true;
    void findTask(taskParam).then((t) => {
      if (!live) return;
      if (t) setDrawer(t);
      else toast.error("That task isn't there any more");
    });
    return () => { live = false; };
  }, [taskParam, tasks, toast]);

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };
  const pickView = (v: View) => { try { localStorage.setItem(VIEW_KEY, v); } catch { /* ignore */ } setParam("view", v); };
  const pickPerson = (v: string) => { storePerson(v); setParam("person", v); };
  const pickKind = (v: Kind) => { try { localStorage.setItem(KIND_KEY, v); } catch { /* ignore */ } setParam("kind", v); };
  const closeDrawer = () => { setDrawer(null); if (taskParam) setParam("task", null); };

  const toggle = async (t: WorkTask) => {
    const wasDone = t.status === "completed";
    setDoneLocal((m) => {
      const n = { ...m };
      if (wasDone) delete n[t.id]; else n[t.id] = { ...t, status: "completed" };
      return n;
    });
    try {
      await api.patch(`/tasks/${t.id}`, { status: wasDone ? "pending" : "completed" });
      void load();
    } catch (err) {
      setDoneLocal((m) => { const n = { ...m }; if (wasDone) n[t.id] = t; else delete n[t.id]; return n; });
      toast.error(err instanceof ApiError ? err.message : "Couldn't update that task");
    }
  };

  // Ticked tasks stay on screen (struck through) until you leave the view.
  const list = useMemo(() => {
    if (!tasks) return null;
    const merged = tasks.map((t) => doneLocal[t.id] ?? t);
    for (const d of Object.values(doneLocal)) if (!tasks.some((t) => t.id === d.id)) merged.push(d);
    return kind === "all" ? merged : merged.filter((t) => isClientTask(t) === (kind === "clients"));
  }, [tasks, doneLocal, kind]);

  const counts = useMemo(() => {
    const open = (list ?? []).filter((t) => t.status !== "completed");
    const today = todayYmd();
    const end = addDays(today, 14);
    return {
      today: open.filter((t) => { const d = dueDay(t); return !d || d <= today; }).length,
      late: open.filter((t) => { const d = dueDay(t); return !!d && d < today; }).length,
      upcoming: open.filter((t) => { const d = dueDay(t); return !!d && d > today && d <= end; }).length,
      routines: open.filter((t) => t.recurrence).length,
      ai: open.filter((t) => t.agent_slug).length,
    };
  }, [list]);

  const defaultsForNew = (extra?: Partial<WorkTask>): Partial<WorkTask> => ({ assignee_id: assignee ?? null, ...(extra ?? {}) });
  const vp: ViewProps = {
    tasks: list ?? [],
    team,
    people,
    wsTz,
    onToggle: (t) => void toggle(t),
    onOpen: (t) => setDrawer(t),
    onNew: (d) => setDrawer({ isNew: true, defaults: defaultsForNew(d) }),
    showOwner: !assignee,
  };
  const tab = (label: string, n: number, warn?: number) => (
    <span className="inline-flex items-center gap-1.5">
      {label}
      {n > 0 && <span className={warn ? "text-rose-300" : "text-zinc-500"}>{n}</span>}
    </span>
  );

  return (
    <div className="mx-auto max-w-5xl space-y-4 pb-12">
      <PageHeader
        eyebrow="Work"
        title="Tasks"
        subtitle="Everything has a day or a rhythm, and an owner. Tick it when it's done."
        actions={
          <>
            <Link to="/work/calendar" className="inline-flex h-9 items-center gap-2 rounded-xl border border-zinc-800 bg-zinc-900/60 px-3 text-sm text-zinc-300 hover:border-indigo-400/40 hover:text-zinc-100">
              <CalendarDays className="h-4 w-4 text-indigo-300" /> Calendar
            </Link>
            <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setDrawer({ isNew: true, defaults: defaultsForNew() })}>New task</Button>
          </>
        }
      />

      <Card className="p-3 sm:p-4">
        <QuickAdd onAdded={load} />
      </Card>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="-mx-1 overflow-x-auto px-1 pb-0.5">
          <Segmented<View>
            value={view}
            onChange={pickView}
            options={[
              { value: "today", label: tab("Today", counts.today, counts.late) },
              { value: "upcoming", label: tab("Upcoming", counts.upcoming) },
              { value: "routines", label: tab("Routines", counts.routines) },
              { value: "ai", label: tab("AI", counts.ai) },
            ]}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Segmented<Kind>
            value={kind}
            onChange={pickKind}
            options={[
              { value: "all", label: "All" },
              { value: "clients", label: "Clients" },
              { value: "mine", label: <span className="whitespace-nowrap">My work</span> },
            ]}
          />
          <div className="-mx-1 max-w-full overflow-x-auto px-1 pb-0.5">
            <PersonSwitch value={person} onChange={pickPerson} people={people} me={me} size="sm" />
          </div>
        </div>
      </div>

      {list === null ? (
        <Card className="space-y-3 p-4">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}
        </Card>
      ) : view === "today" ? (
        <TodayView {...vp} />
      ) : view === "upcoming" ? (
        <UpcomingView {...vp} />
      ) : view === "routines" ? (
        <RoutinesView {...vp} />
      ) : (
        <AiView {...vp} />
      )}

      <TaskDrawer target={drawer} onClose={closeDrawer} onChanged={load} />
    </div>
  );
}
